import { observer } from 'mobx-react-lite';

import _ from 'lodash';
import { makeAutoObservable, runInAction } from 'mobx';
import { useEffect } from 'react';
import { useParams } from 'react-router';
import {
    StockIndexResponse,
    StockResponse,
    StockSectorResponse,
} from '../../../server/response/responseSchema';
import modalStore from '../../stores/modalStore';
import Box from '../../uikit/Box';
import Frame from '../../uikit/Frame';
import { apiClient } from '../../clientUtils/apiClient';
import { useMobxInstance } from '../../clientUtils/mobxUtil';
import ChangeBarGraph, { ChangeBarGraphItem } from './ChangeBarGraph';
import TargetReturnButton from './TargetReturnButton';

class ViewStore {
    sectorSlug: string;

    sector: StockSectorResponse | null = null;
    sectorHoldings: StockResponse[] | null = null;

    // 기준 지표 데이터
    indexPerformance: StockIndexResponse[] = [];
    sectorPerformance: StockSectorResponse[] = [];
    briefDate: string | null = null;
    targetReturn: 'daily' | 'mtd' | 'ytd' = 'daily';

    // 그래프를 그릴 데이터 (지표와 합산한 데이터)
    graphItems: ChangeBarGraphItem<{ ticker: string }>[] = [];

    isLoading: boolean = true;

    constructor(sectorSlug: string) {
        this.sectorSlug = sectorSlug;
        makeAutoObservable(this);
    }

    async fetch() {
        this.isLoading = true;

        try {
            const [sector, sectorHoldings, indexPerformance, sectorPerformance] = await Promise.all(
                [
                    apiClient.stockSector.public.getSectorBySlug.$query({
                        sectorSlug: this.sectorSlug,
                    }),
                    apiClient.stockSector.public.getSectorHoldings.$query({
                        sectorSlug: this.sectorSlug,
                    }),
                    apiClient.stockIndex.public.getIndexPerformance.$query(),
                    apiClient.stockSector.public.getSectorPerformance.$query(),
                ],
            );

            runInAction(() => {
                this.sector = sector;
                this.sectorHoldings = sectorHoldings as StockResponse[];
                this.indexPerformance = indexPerformance;
                this.sectorPerformance = sectorPerformance;
                this.briefDate = _.last(indexPerformance)?.price?.date || null;

                this.makeGraphItems();
            });
        } catch (error) {
            console.error('섹터 상세 정보 조회 실패:', error);
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    private makeGraphItems() {
        // S&P 500 변화율 추출
        const sp500Index = this.indexPerformance.find((index) => index.ticker === '_SP500');
        let sp500Return = 0;
        if (this.targetReturn === 'daily') {
            sp500Return = Number(sp500Index?.price?.dailyReturn) || 0;
        } else if (this.targetReturn === 'mtd') {
            sp500Return = Number(sp500Index?.price?.mtdReturn) || 0;
        } else if (this.targetReturn === 'ytd') {
            sp500Return = Number(sp500Index?.price?.ytdReturn) || 0;
        }

        // 현재 섹터 변화율 추출 (slug로 매칭)
        const currentSector = this.sectorPerformance.find(
            (sector) => sector.slug === this.sectorSlug,
        );
        let sectorReturn = 0;
        if (this.targetReturn === 'daily') {
            sectorReturn = Number(currentSector?.price?.dailyReturn) || 0;
        } else if (this.targetReturn === 'mtd') {
            sectorReturn = Number(currentSector?.price?.mtdReturn) || 0;
        } else if (this.targetReturn === 'ytd') {
            sectorReturn = Number(currentSector?.price?.ytdReturn) || 0;
        }

        // items 배열 생성: 종목들 + S&P500 + 현재 섹터
        this.graphItems = _.compact([
            // 종목들
            ...(this.sectorHoldings?.map((holding) => {
                let targetReturn = 0;
                if (this.targetReturn === 'daily') {
                    targetReturn = Number(holding.price?.dailyReturn) || 0;
                } else if (this.targetReturn === 'mtd') {
                    targetReturn = Number(holding.price?.mtdReturn) || 0;
                } else if (this.targetReturn === 'ytd') {
                    targetReturn = Number(holding.price?.ytdReturn) || 0;
                }

                return {
                    name: holding.ticker,
                    changePercent: targetReturn,
                    data: {
                        ticker: holding.ticker,
                    },
                };
            }) || []),
            // S&P500
            {
                name: 'S&P500',
                changePercent: sp500Return,
                isBenchmark: true,
            },
            // 현재 섹터
            {
                name: this.sector?.nameKo || '현재 섹터',
                changePercent: sectorReturn,
                isBenchmark: true,
            },
        ]);
    }

    changeTargetReturn(target: 'daily' | 'mtd' | 'ytd') {
        this.targetReturn = target;
        this.makeGraphItems();
    }

    get highlightPercent() {
        return this.targetReturn === 'daily' ? 3 : this.targetReturn === 'mtd' ? 10 : 20;
    }
}

const SectorHoldingsPage = observer(() => {
    const { sectorSlug } = useParams<{ sectorSlug: string }>();
    const viewStore = useMobxInstance(ViewStore, sectorSlug as string);

    useEffect(() => {
        viewStore.fetch();
    }, [viewStore]);

    return (
        <Frame>
            <Frame.Title title={viewStore.sector?.nameKo || ''} isLoading={viewStore.isLoading} />
            <Frame.Description>{viewStore.sector?.description}</Frame.Description>
            <Box isLoading={viewStore.isLoading}>
                <Box.Title title="시가총액 상위 10개 종목" />
                <ChangeBarGraph<{ ticker: string }>
                    items={viewStore.graphItems}
                    highligthPercent={viewStore.highlightPercent}
                    className="-mx-5"
                    isShowArrow={(item) => {
                        return !item.isBenchmark;
                    }}
                    onClickBar={(item) => {
                        if (item.isBenchmark) {
                            return;
                        }

                        const ticker = item.data?.ticker;
                        if (ticker) {
                            modalStore.stock({
                                ticker,
                            });
                        }
                    }}
                />
                {viewStore.briefDate && (
                    <TargetReturnButton
                        className="mt-8"
                        targetDate={viewStore.briefDate}
                        targetReturn={viewStore.targetReturn}
                        onChangeTargetReturn={(target) => viewStore.changeTargetReturn(target)}
                    />
                )}
            </Box>
        </Frame>
    );
});

export default SectorHoldingsPage;
